import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@/shared/ui/components/button';
import MoneyAmount from '@/shared/ui/components/money-amount';
import {
  getPayInFullOption,
  isPayInFullOnlyAvailable,
} from '@/modules/decline-ui/lib/decline-ui-helpers';
import type { AppContext } from '@/types';
import * as events from '@/events';
import { emitErrorTracking } from '@/shared/tracking/analytics';
import { emitDeclineUiBackToMerchantClickedTracking } from '@/shared/tracking/analytics-decline-ui-events';
import { wait } from '@/lib/common';
import { logInfo } from '@/services/logging/logger';

interface DeclineUiFooterProps {
  ctx: AppContext;
  onPayInFull: () => void;
  processing: boolean;
}

export function DeclineUiFooter({
  ctx,
  onPayInFull,
  processing,
}: DeclineUiFooterProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [redirecting, setRedirecting] = useState(false);

  const showPayInFull = useMemo(
    () => isPayInFullOnlyAvailable(ctx.session.paymentOptions ?? []),
    [ctx.session.paymentOptions],
  );

  const cashbackAmount = useMemo(() => {
    return (
      getPayInFullOption(ctx.session.paymentOptions ?? [])?.cashback
        ?.actualAmount?.amount ?? 0
    );
  }, [ctx.session.paymentOptions]);

  const handleBackToMerchant = useCallback(async () => {
    if (redirecting) {
      return;
    }

    setRedirecting(true);
    const redirectTo = ctx.session.merchant.urls.failure;

    const backToMerchantPayload = {
      checkout_id: ctx.session.id,
      order_id: ctx.session.order?.id ?? '',
    };
    emitDeclineUiBackToMerchantClickedTracking(ctx.session);
    ctx.sendEvent(
      events.DECLINE_UI_BACK_TO_MERCHANT_CLICKED,
      backToMerchantPayload,
    );

    try {
      logInfo(
        'decline-ui',
        `Redirecting to Merchant's Failure URL: ${redirectTo}`,
        {
          checkoutId: ctx.session.id,
        },
      );

      ctx.sendMobileMessage('instore_checkout_failure', 'back');

      emitErrorTracking('go_back_to_merchant_clicked', ctx.session, {
        error_type: 'credit_decline',
      });
    } catch {
      // Redirect should still proceed.
    }

    await wait(2000);
    window.location.href = redirectTo;
  }, [ctx, redirecting]);

  return (
    <div className="flex w-full flex-col gap-4 rtl:font-arabic">
      {showPayInFull && (
        <div className="relative w-full">
          {cashbackAmount > 0 && (
            <div className="absolute -top-3 right-0 z-[1] rounded-[8px] border border-dashed border-[#9600F1] bg-[#F5E3FF] px-[6px] py-[3px] text-[12px] font-semibold leading-4 tracking-[-0.18px] text-[#2D0048]">
              <MoneyAmount
                amount={cashbackAmount}
                currency={ctx.session.order.currency}
                language={i18n.language}
                className="inline-flex"
              />{' '}
              {t('decline_ui.actions.cashback_suffix')}
            </div>
          )}
          <Button
            variant="primary"
            size="large"
            className="w-full !bg-[#2D0048] hover:!bg-[#2D0048]"
            loading={processing}
            disabled={processing || redirecting}
            onClick={onPayInFull}
          >
            {t('decline_ui.actions.pay_in_full_now')}
          </Button>
        </div>
      )}

      <Button
        variant="neutral"
        size="large"
        className="w-full !bg-[#EBEEF4] hover:!bg-[#EBEEF4] !text-[#16181D]"
        loading={redirecting}
        disabled={redirecting || processing}
        onClick={handleBackToMerchant}
      >
        {t('decline_ui.actions.back_to_merchant')}
      </Button>
    </div>
  );
}
